import { Col, Flex, Form, InputNumber, Row } from "antd"
import { useTranslation } from "react-i18next"
import PropTypes from "prop-types"
import { TRIP_PRICE } from "../../utils/constants"
import { InfoTooltip } from "ui/Tooltip/InfoTooltip"
import { AllowedTypesBlock } from "./AllowedTypesBlock"
import { spacings } from "themes/variables"
import { useEffect, useState } from "react"
import { ExtraGuestsBlock } from "./ExtraGuestsBlock"
import { useSelector } from "react-redux"
import { modelSelector } from "store/selectors"

export const FlatRateFormItems = ({ form, record }) => {
  const [t] = useTranslation()
  const [childrenAllowed, setChildrenAllowed] = useState(record?.children_allowed)
  const [infantsAllowed, setInfantsAllowed] = useState(record?.infants_allowed)
  const [passengersAllowed, setPassengersAllowed] = useState(record?.passengers_allowed)

  // Watch form values for min_guests and max_guests to get real-time updates
  const min_guests = Form.useWatch('min_guests', form)
  const max_guests = Form.useWatch('max_guests', form)

  // Fallback to Redux store if form values are not yet set
  const trip = useSelector(modelSelector("trip"))
  const minGuestsValue = min_guests ?? trip?.min_guests
  const maxGuestsValue = max_guests ?? trip?.max_guests

  useEffect(() => {
    form.setFieldsValue({
      [TRIP_PRICE.FLAT_RATE]: record?.[TRIP_PRICE.FLAT_RATE],
      [TRIP_PRICE.GUESTS_INCLUDED]: record?.[TRIP_PRICE.GUESTS_INCLUDED] || maxGuestsValue,
      [TRIP_PRICE.CHILDREN_ALLOWED]: record?.[TRIP_PRICE.CHILDREN_ALLOWED],
      [TRIP_PRICE.INFANTS_ALLOWED]: record?.[TRIP_PRICE.INFANTS_ALLOWED],
      [TRIP_PRICE.PASSENGERS_ALLOWED]: record?.[TRIP_PRICE.PASSENGERS_ALLOWED],
      [TRIP_PRICE.PRICE_ADULT]: record?.[TRIP_PRICE.PRICE_ADULT],
      [TRIP_PRICE.PRICE_CHILD]: record?.[TRIP_PRICE.PRICE_CHILD],
      [TRIP_PRICE.PRICE_INFANT]: record?.[TRIP_PRICE.PRICE_INFANT],
      [TRIP_PRICE.PRICE_PASSENGER]: record?.[TRIP_PRICE.PRICE_PASSENGER],
    })
  }, [record, form, maxGuestsValue])

  return (
    <Flex vertical gap={spacings.XL}>
      <Row gutter={16}>
        <Col span={12} md={12} sm={24}>
          <Form.Item
            name={TRIP_PRICE.FLAT_RATE}
            label={t("experiences.scheduling.sections.length_and_rates.set_flat_rate")}
            rules={[{ required: true, message: t("form_validation.cannot_be_blank") }]}
            className="mb-0"
          >
            <InputNumber prefix="$" size="large" placeholder="100" min={0} className="w-100" />
          </Form.Item>
        </Col>
        <Col span={12} md={12} sm={24}>
          <Form.Item
            name={TRIP_PRICE.GUESTS_INCLUDED}
            label={
              <Flex align="center" gap="small">
                {t("experiences.scheduling.sections.length_and_rates.guests_included")}
                <InfoTooltip title={t("experiences.scheduling.sections.length_and_rates.guests_included_info")} />
              </Flex>
            }
            rules={[
              { required: true, message: t("form_validation.cannot_be_blank") },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (typeof value === "number" && value > maxGuestsValue) {
                    return Promise.reject(new Error(t("experiences.scheduling.sections.length_and_rates.guests_included_max_error")))
                  }
                  if (typeof value === "number" && value < minGuestsValue) {
                    return Promise.reject(new Error(t("experiences.scheduling.sections.length_and_rates.guests_included_min_error")))
                  }
                  return Promise.resolve()
                },
              }),
            ]}
            className="mb-0"
          >
            <InputNumber
              size="large"
              placeholder={maxGuestsValue}
              className="w-100"
            />
          </Form.Item>
        </Col>
      </Row>
      <AllowedTypesBlock
        setChildrenAllowed={setChildrenAllowed}
        setInfantsAllowed={setInfantsAllowed}
        setPassengersAllowed={setPassengersAllowed}
      />
      <ExtraGuestsBlock
        form={form}
        childrenAllowed={childrenAllowed}
        infantsAllowed={infantsAllowed}
        passengersAllowed={passengersAllowed}
        maxGuests={maxGuestsValue}
      />
    </Flex>
  )
}

FlatRateFormItems.propTypes = {
  form: PropTypes.shape({
    setFieldValue: PropTypes.func.isRequired,
    setFieldsValue: PropTypes.func.isRequired,
  }).isRequired,
  record: PropTypes.shape({
    children_allowed: PropTypes.bool,
    infants_allowed: PropTypes.bool,
    passengers_allowed: PropTypes.bool,
  }),
}